/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  PRMarinaNodeArtboard,
  PRMarinaLayer,
  PRMarinaLayerStyle,
  PRMarinaNode,
  PRMarinaRect,
  PRStackPadding,
} from '@sketch-hq/sketch-web-renderer'
import {
  ArtboardPreset,
  GridSettings,
  LayerStyle,
  ElementType,
  LayoutSettings,
  SketchFrameElement,
  SketchElement,
  SketchElementBase,
  SketchElementType,
  SketchLayerElement,
  LayerStyleProperties,
  GroupStyleProperties,
  SketchSymbolMasterFrameElement,
  SketchSymbolInstanceLayerElement,
  SketchLayerElementBase,
  TextLayerStyleProperties,
  WithPotentialSharedStyle,
  WithSharedStyle,
  LayerCorners,
  NearestOpenableFrame,
} from '../../../../../../inspector'
import {
  transformAttributedString,
  transformCorners,
  transformExportFormatsComponent,
  transformPresentationAppearance,
  transformPresentationBlur,
  transformPresentationBorderOptions,
  transformPresentationBorders,
  transformPresentationFills,
  transformPresentationTints,
  transformPresentationShadows,
  transformSharedStyleReference,
  transformSymbolMasterReference,
  transformTextAttributes,
  transformVariantConfiguration,
} from './presentationFileTransformUtils'
import {
  mapCrossAxisAlignment,
  mapDirection,
  mapLayerType,
  mapMainAxisAlignment,
  mapVerticalTextAlignment,
} from './presentationFileTransformUtils/presentationMarinaEnumsMapping'
import { computeSymbolMasterNames } from './utils'
import { StackProperties } from '../../../../../../inspector/types/InspectorTypes/Stack'

/** Props needed to create the element node */
type CreateSketchElementContext = {
  /**
   * The symbol instance element we are currently inside, if any.
   */
  symbolInstanceAncestorElement?: SketchSymbolInstanceLayerElement
  /**
   * The closest frame ancestor element we are currently inside, if any.
   * (not necessarily the top-level frame)
   */
  frameAncestorElement?: SketchFrameElement
  /**
   * The nearest openable frame for this element.
   * it can be a top level frame (frame or symbol master), or a symbol instance's master,
   * or undefined if there is no such frame in the ancestor chain.
   */
  nearestOpenableFrame?: NearestOpenableFrame
  /**
   * The bounds of the container node that can be Page is we are looking at the page view
   * or artboard if we are looking at an individual artboard.
   */
  containerNodeBounds: PRMarinaRect
  rootNodeBounds: PRMarinaRect
  isMaskedByOtherLayer: boolean
  isInsideLockedParent: boolean
  isInsideGroupWithExports: boolean
  isInsideGroupWithStyles: boolean
  /**
   * The parent element in the tree hierarchy.
   */
  parentElement?: SketchElement
}

export function createSketchElement(
  marinaNode: PRMarinaNode,
  context: CreateSketchElementContext
): SketchElement {
  if (marinaNode.isPage()) {
    return {
      ...createSketchLayerElement(marinaNode, context),
      type: ElementType.Page,
    }
  }

  if (marinaNode.isFrame() || marinaNode.isTopLevelFrame()) {
    return createSketchFrameElement(marinaNode, context)
  }

  return createSketchLayerElement(marinaNode, context)
}

export function getSketchElementBaseProperties(
  marinaNode: PRMarinaNode,
  context: Pick<
    CreateSketchElementContext,
    | 'containerNodeBounds'
    | 'frameAncestorElement'
    | 'nearestOpenableFrame'
    | 'parentElement'
    | 'symbolInstanceAncestorElement'
    | 'rootNodeBounds'
    | 'isInsideLockedParent'
  >
): SketchElementBase {
  const {
    containerNodeBounds,
    rootNodeBounds,
    frameAncestorElement,
    nearestOpenableFrame,
    parentElement,
    symbolInstanceAncestorElement,
  } = context
  const prRectBounds = marinaNode.getAbsoluteLayerBounds()
  const prMarinaLayer = marinaNode.getLayer()
  const layerSize = prMarinaLayer?.bounds
  const elementUUID = marinaNode.getLayerUUID()

  const exportsProps = getExportsProps(
    marinaNode,
    Boolean(symbolInstanceAncestorElement)
  )

  const isTopLevelNode =
    !parentElement || Boolean(parentElement.prMarinaNode.isPage())

  const layerPathHash = marinaNode.getLayerPathHash()

  const isAncestorHidden = Boolean(
    parentElement?.isHidden || parentElement?.isAncestorHidden
  )

  const isLocked = prMarinaLayer?.locked || false
  const isAncestorLocked = context.isInsideLockedParent

  return {
    prMarinaNode: marinaNode,
    identifier: marinaNode.getIdentifier(),
    name: marinaNode.getLayerName(),
    elementUUID: elementUUID,
    isHidden: !marinaNode.isEnabled(),
    isAncestorHidden,
    isLocked,
    isAncestorLocked,
    children: [],
    parent: parentElement,
    // We want the bounds to be relative to the artboard and not the canvas if the root of the tree is just an artboard.
    rootRelativeBoxRect: {
      x: prRectBounds.x - rootNodeBounds.x,
      y: prRectBounds.y - rootNodeBounds.y,
      width: prRectBounds.width,
      height: prRectBounds.height,
    },
    frameRelativeBoxRect: {
      x: prRectBounds.x - containerNodeBounds.x,
      y: prRectBounds.y - containerNodeBounds.y,
      width: prRectBounds.width,
      height: prRectBounds.height,
    },
    layerSize: {
      width: layerSize?.width ?? 0,
      height: layerSize?.height ?? 0,
      widthBehaviour: prMarinaLayer?.widthSizingBehaviour,
      heightBehaviour: prMarinaLayer?.heightSizingBehaviour,
    },
    frameAncestorElement,
    nearestOpenableFrame,
    layerPathHash: layerPathHash,
    isTopLevelNode,
    ...exportsProps,
  }
}

function getExportsProps(
  marinaNode: PRMarinaNode,
  isInsideSymbolInstance: boolean
): Pick<SketchElementBase, 'isExportable' | 'exportFormats' | 'exportId'> {
  // Exports is not supported inside symbol instances. This could potentially
  // be supported in the future but in the current state, the data is present
  // in the presentation file but we want to ignore it. The main reasons are:
  // - This is the legacy behavior. We don't want to change it for now.
  // - We would need to adapt the code to be able to fetch assets from a different document
  // when the symbol is external
  if (isInsideSymbolInstance) {
    return {
      exportFormats: [],
      isExportable: false,
      exportId: undefined,
    }
  }

  const prExportFormats = marinaNode.getExportFormats()
  const nodeUUID = marinaNode.getLayerUUID()

  const exportFormats = prExportFormats
    ? transformExportFormatsComponent(prExportFormats)
    : []
  const isExportable = exportFormats.length > 0
  const exportId = nodeUUID

  return {
    exportFormats,
    isExportable,
    exportId,
  }
}

/** FRAME */

export function createSketchFrameElement(
  marinaNode: PRMarinaNode,
  /**
   * The bounds of the root artboard.
   */
  context: CreateSketchElementContext
): SketchFrameElement {
  const prMarinaFrame = marinaNode.getFrame()
  const prMarinaLayer = marinaNode.getLayer()

  if (!prMarinaFrame) {
    throw new Error('Frame component must be present')
  }

  if (!prMarinaLayer) {
    throw new Error('LayerInfo component must be present')
  }

  const type = marinaNode.isGraphic()
    ? ElementType.Graphic
    : mapLayerType(prMarinaLayer.type.value)

  if (
    type !== ElementType.Artboard &&
    type !== ElementType.SymbolMaster &&
    type !== ElementType.Graphic
  ) {
    throw new Error('The layer type must be Artboard or SymbolMaster')
  }

  const style = getLayerStyle(prMarinaLayer, type)
  const corners = getCorners(prMarinaLayer)

  const parentNode = marinaNode.getParentNode()
  const isTopLevelFrame = !parentNode || Boolean(parentNode?.isPage())

  /**
   * Compute the nearest openable frame for this frame element.
   */
  function computeNearestOpenableFrame(): NearestOpenableFrame | undefined {
    if (type === ElementType.SymbolMaster) {
      return {
        type: ElementType.SymbolMaster,
        targetUUID: marinaNode.getLayerUUID(),
        isSelf: true,
      }
    }
    if (isTopLevelFrame) {
      return {
        type: ElementType.Artboard,
        targetUUID: marinaNode.getLayerUUID(),
        isSelf: true,
      }
    }

    // Nested frame: inherit from context
    return context.nearestOpenableFrame
      ? {
          ...context.nearestOpenableFrame,
          // Not self, since we are inheriting from context
          isSelf: false,
        }
      : undefined
  }

  const nearestOpenableFrame = computeNearestOpenableFrame()

  const baseProperties = getSketchElementBaseProperties(marinaNode, {
    containerNodeBounds: context.containerNodeBounds,
    frameAncestorElement: context.frameAncestorElement,
    nearestOpenableFrame,
    parentElement: context.parentElement,
    symbolInstanceAncestorElement: context.symbolInstanceAncestorElement,
    rootNodeBounds: context.rootNodeBounds,
    isInsideLockedParent: context.isInsideLockedParent,
  })

  const frameElement = {
    ...baseProperties,
    identifier: marinaNode.getIdentifier(),
    rotation: prMarinaLayer.rotation,
    appearance: style?.appearance,
    gridSettings: getGridSettings(prMarinaFrame),
    layoutSettings: getLayoutSettings(prMarinaFrame),
    preset: getArtboardPreset(prMarinaFrame),
    hasFragmentPending: marinaNode.hasFragmentPending(),
    stack: getStackProperties(marinaNode),
    frameAncestorElement: context.frameAncestorElement,
    isTopLevelFrame,
    corners,
  }

  if (type === ElementType.Graphic) {
    return {
      ...frameElement,
      type: ElementType.Graphic,
      style: style as WithPotentialSharedStyle<LayerStyleProperties>,
    }
  }

  if (type === ElementType.Artboard) {
    return {
      ...frameElement,
      type: ElementType.Artboard,
      style: style as WithPotentialSharedStyle<LayerStyleProperties>,
    }
  }

  const symbolMasterElement: SketchSymbolMasterFrameElement = {
    ...frameElement,
    type: ElementType.SymbolMaster,
    ...computeSymbolMasterNames(frameElement.name),
    style: style as WithPotentialSharedStyle<LayerStyleProperties>,
    variantConfiguration: transformVariantConfiguration(
      marinaNode.getVariantConfiguration()
    ),
  }

  return symbolMasterElement
}

function getCorners(layer: PRMarinaLayer): LayerCorners | undefined {
  const prCorners = layer?.getCorners()
  return prCorners ? transformCorners(prCorners) : undefined
}

function getGridSettings(prMarinaArtboard: PRMarinaNodeArtboard): GridSettings {
  const defaultGridSettings: GridSettings = {
    isEnabled: false,
    size: 20,
    thickLineStep: 10,
  }
  const prArtboardGrid = prMarinaArtboard.getGrid()

  const gridSettings: GridSettings | undefined = prArtboardGrid
    ? {
        isEnabled: prArtboardGrid.isEnabled,
        size: prArtboardGrid.size,
        thickLineStep: prArtboardGrid.thickGridTimes,
      }
    : undefined

  return {
    ...defaultGridSettings,
    ...gridSettings,
  }
}

function getLayoutSettings(
  prMarinaArtboard: PRMarinaNodeArtboard
): LayoutSettings {
  const prArtboardLayout = prMarinaArtboard.getLayout()
  const defaultLayoutSettings: LayoutSettings = {
    columnWidth: 60,
    drawHorizontal: false,
    drawHorizontalLines: false,
    drawVertical: true,
    gutterHeight: 12,
    gutterWidth: 20,
    guttersOutside: true,
    horizontalOffset: 0,
    isEnabled: false,
    numberOfColumns: 5,
    numberOfRows: 0,
    rowHeightMultiplication: 5,
    totalWidth: 0,
  }

  const layoutSettings: LayoutSettings | undefined = prArtboardLayout
    ? {
        columnWidth: prArtboardLayout.columnWidth,
        drawHorizontal: prArtboardLayout.drawHorizontal,
        drawHorizontalLines: prArtboardLayout.drawHorizontalLines,
        drawVertical: prArtboardLayout.drawVertical,
        gutterHeight: prArtboardLayout.gutterHeight,
        gutterWidth: prArtboardLayout.gutterWidth,
        guttersOutside: prArtboardLayout.gutterOutside,
        horizontalOffset: prArtboardLayout.horizontalOffset,
        isEnabled: prArtboardLayout.isEnabled,
        numberOfColumns: prArtboardLayout.columnsCount,
        numberOfRows: 0,
        rowHeightMultiplication: prArtboardLayout.rowHeightMultiplication,
        totalWidth: prArtboardLayout.totalWidth,
      }
    : undefined

  return {
    ...defaultLayoutSettings,
    ...layoutSettings,
  }
}

function getArtboardPreset(
  prMarinaArtboard: PRMarinaNodeArtboard
): ArtboardPreset {
  const presetBounds = prMarinaArtboard.templateBounds

  return {
    name: prMarinaArtboard.templateName,
    width: presetBounds.width,
    height: presetBounds.height,
  }
}

function getStackPadding(padding: PRStackPadding) {
  return {
    top: padding.top,
    right: padding.right,
    bottom: padding.bottom,
    left: padding.left,
  }
}

function getStackProperties(marinaNode: PRMarinaNode): StackProperties | null {
  const stack = marinaNode.getStack()
  const stackItem = marinaNode.getStackItem()

  if (!stack) {
    return null
  }

  return {
    direction: mapDirection(stack.direction.value),
    gap: stack.gap,
    crossAxisAlignment: mapCrossAxisAlignment(stack.crossAxisAlignment.value),
    crossAxisGutterGap: stack.crossAxisGutterGap,
    mainAxisAlignment: mapMainAxisAlignment(stack.mainAxisAlignment.value),
    alignContent: mapMainAxisAlignment(stack.alignContent.value),
    padding: getStackPadding(stack.padding),
    wrappingEnabled: stack.wrappingEnabled,
  }
}

/** LAYER */

export function createSketchLayerElement(
  marinaNode: PRMarinaNode,
  context: CreateSketchElementContext
): SketchLayerElement {
  const {
    isMaskedByOtherLayer,
    containerNodeBounds,
    symbolInstanceAncestorElement,
    frameAncestorElement,
  } = context
  const prMarinaLayer = marinaNode.getLayer()

  if (!prMarinaLayer) {
    throw Error('LayerInfo component must be present')
  }

  const type = mapLayerType(prMarinaLayer.type.value)

  if (type === ElementType.Page || type === ElementType.Undefined) {
    throw Error(
      `Illegal layer type "${type}" encountered while creating SketchLayerElement. (Layer name: ${marinaNode.getName()}, uuid: ${marinaNode.getLayerUUID()}, Frame ancestor: ${
        frameAncestorElement?.name
      })`
    )
  }

  const style = getLayerStyle(prMarinaLayer, type)
  const corners = getCorners(prMarinaLayer)

  const prAttributedString = prMarinaLayer.getAttributedString()
  const text = prAttributedString
    ? transformAttributedString(prAttributedString)
    : undefined

  /**
   * Compute the nearest openable frame for this layer element.
   * - Symbol instance: points to its symbol master
   * - Layer inside a symbol instance: points symbol's master of that symbol instance
   * - Otherwise: inherits from context
   */
  function computeNearestOpenableFrame(
    symbolMasterRef?: ReturnType<typeof transformSymbolMasterReference>
  ): NearestOpenableFrame | undefined {
    // Symbol instance: open its symbol master
    if (type === ElementType.SymbolInstance && symbolMasterRef?.artboardUuid) {
      return {
        type: ElementType.SymbolInstance,
        targetUUID: symbolMasterRef.artboardUuid,
        isSelf: true,
      }
    }
    // Layer inside a symbol instance: open the parent symbol's master
    if (symbolInstanceAncestorElement?.symbolMaster?.artboardUuid) {
      return {
        type: ElementType.SymbolInstance,
        targetUUID: symbolInstanceAncestorElement.symbolMaster.artboardUuid,
        isSelf: false,
      }
    }

    return context.nearestOpenableFrame
      ? {
          ...context.nearestOpenableFrame,
          // Not self, since we are inheriting from context
          isSelf: false,
        }
      : undefined
  }

  const prSymbolMasterReference =
    type === ElementType.SymbolInstance
      ? marinaNode.getSymbolMasterReference()
      : undefined

  const symbolMaster =
    prSymbolMasterReference && type === ElementType.SymbolInstance
      ? transformSymbolMasterReference(
          prSymbolMasterReference,
          marinaNode.getVariantConfiguration()
        )
      : undefined

  const nearestOpenableFrame = computeNearestOpenableFrame(symbolMaster)

  const layerElement: SketchLayerElementBase = {
    ...getSketchElementBaseProperties(marinaNode, {
      containerNodeBounds,
      frameAncestorElement,
      nearestOpenableFrame,
      parentElement: context.parentElement,
      symbolInstanceAncestorElement: symbolInstanceAncestorElement,
      rootNodeBounds: context.rootNodeBounds,
      isInsideLockedParent: context.isInsideLockedParent,
    }),
    isLayerMasked: isMaskedByOtherLayer,
    symbolInstanceAncestorElement: symbolInstanceAncestorElement,
    corners,
    rotation: prMarinaLayer.rotation,
    appearance: style?.appearance,
    text,
    isInsideGroupWithExports: context.isInsideGroupWithExports,
    isInsideGroupWithStyles: context.isInsideGroupWithStyles,
  }

  if (type === ElementType.Hotspot || type === ElementType.Slice) {
    return { ...layerElement, type, style: undefined }
  }

  if (type === ElementType.Group) {
    return {
      ...layerElement,
      type,
      style: style as GroupStyleProperties,
    }
  }

  if (type === ElementType.Text) {
    return {
      ...layerElement,
      type,
      style: style as WithPotentialSharedStyle<TextLayerStyleProperties>,
    }
  }

  if (type === ElementType.SymbolInstance) {
    if (!prSymbolMasterReference || !symbolMaster) {
      throw Error(
        'SymbolInstance element encountered without a SymbolMasterReference'
      )
    }

    return {
      ...layerElement,
      type,
      style: style as GroupStyleProperties,
      symbolMaster,
    }
  }

  return {
    ...layerElement,
    type: type as any,
    style: style as WithPotentialSharedStyle<LayerStyleProperties>,
    // A symbol master nested inside a frame (e.g. the individual variants of a
    // variant set) is built here rather than in `createSketchFrameElement`,
    // because `isFrame()`/`isTopLevelFrame()` are both false for a non
    // top-level master. It's still an `ElementType.SymbolMaster` (a frame
    // element per `isFrameElement`), so it must carry its variant configuration
    // just like a top-level master does.
    ...(type === ElementType.SymbolMaster && {
      variantConfiguration: transformVariantConfiguration(
        marinaNode.getVariantConfiguration()
      ),
    }),
  }
}

/* UTILS */

function getLayerStyle(
  prMarinaLayer: PRMarinaLayer,
  type: SketchElementType
): LayerStyle | undefined {
  const prStyle = prMarinaLayer.getStyle()
  const prLayerStyle = prStyle?.getLayerStyle()

  if (!prStyle || !prLayerStyle) {
    return
  }

  const prOriginalSharedStyleValues = prStyle.getOriginalSharedStyleValues()
  const prSharedStyleReference = prStyle.getSharedStyleReference()
  const isDirty = prStyle.isDirty

  const basicStyleProps = getStyleProperties(prLayerStyle)

  const originalSharedStyleValues = prOriginalSharedStyleValues
    ? getStyleProperties(prOriginalSharedStyleValues)
    : undefined

  const sharedStyleReference = prSharedStyleReference
    ? transformSharedStyleReference(prSharedStyleReference)
    : undefined

  if (type === ElementType.Group) {
    const groupStyle: GroupStyleProperties = {
      isDirty,
      fills: basicStyleProps.fills ?? [],
      tints: basicStyleProps.tints ?? [],
      shadows: basicStyleProps.shadows ?? [],
      appearance: basicStyleProps.appearance,
    }

    return groupStyle
  }

  if (sharedStyleReference) {
    const styleWithSharedStyle: WithSharedStyle<LayerStyleProperties> = {
      ...basicStyleProps,
      isDirty,
      originalSharedStyleValues,
      sharedStyleReference,
    }
    return styleWithSharedStyle
  }

  return basicStyleProps
}

export function getStyleProperties(prMarinaLayerStyle: PRMarinaLayerStyle) {
  const prBlur = prMarinaLayerStyle.getBlur()
  const prFills = prMarinaLayerStyle.fills
  const prTints = prMarinaLayerStyle.tints
  const prAppearance = prMarinaLayerStyle.appearance
  const prBorders = prMarinaLayerStyle.borders
  const prShadows = prMarinaLayerStyle.shadows
  const prBorderOptions = prMarinaLayerStyle.borderOptions
  const prInnerShadows = prMarinaLayerStyle.innerShadows
  const prVerticalTextAlignment = prMarinaLayerStyle.verticalTextAlignment
  const prText = prMarinaLayerStyle.getTextAttributes()

  const tints = prTints ? transformPresentationTints(prTints) : []
  const fills = prFills ? transformPresentationFills(prFills) : []
  const blur = prBlur ? transformPresentationBlur(prBlur) : undefined
  const appearance = prAppearance
    ? transformPresentationAppearance(prAppearance)
    : undefined
  const shadows = prShadows ? transformPresentationShadows(prShadows) : []
  const borders = prBorders ? transformPresentationBorders(prBorders) : []
  const borderOptions = prBorderOptions
    ? transformPresentationBorderOptions(prBorderOptions)
    : undefined
  const innerShadows = prInnerShadows
    ? transformPresentationShadows(prInnerShadows)
    : []
  const verticalAlignment = mapVerticalTextAlignment(
    prVerticalTextAlignment.value
  )
  const text = prText ? transformTextAttributes(prText)?.attributes : undefined

  return {
    tints,
    fills,
    blur,
    appearance,
    borders,
    borderOptions,
    shadows,
    innerShadows,
    verticalAlignment,
    text,
  } as const
}
